import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Download } from 'lucide-react'

import { Button } from '@/components/atoms/Button'
import { NavLink } from '@/components/atoms/NavLink'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/atoms/Select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/atoms/Table'
import { type JudicialRecord } from '@/components/model'
import { AdminHeader } from '@/components/organisms/AdminHeader'
import { JudicialVolunteerSearchForm } from '@/components/organisms/JudicialVolunteerSearchForm'
import {
  JudicialPreanalysisListView,
  type JudicialPreanalysisListViewProps,
} from '@/components/templates/JudicialPreanalysisListView'
import { decisionTextColor, useDecisionText } from '@/components/utils/judicialDecision'
import { cn } from '@/lib/utils'
import { type JudicialFilter, type JudicialTab } from '@/routes/judicial.index'

import { volunteerPiloteUrl } from '../../utils/volunteerPiloteUrl'

import { MunicipalSearchStatusCell } from './MunicipalSearchStatusCell'
import { ProvincialSearchStatusCell } from './ProvincialSearchStatusCell'
import { useExportJudicialRecordsCsv } from './useExportJudicialRecordsCsv'

dayjs.extend(utc)

export interface JudicialResultListViewProps {
  records: JudicialRecord[]
  regionName?: string
  onBackToPiloteClicked?: () => void | Promise<void>
  onCandidateSearch: (volunteerCodes: string) => Promise<void> | void
  className?: string
  requestedResultsCount: number
  queriedCandidatesCount: number
  pricePerSearch: number
  pricePerRecord: number
  selectedFilter: JudicialFilter
  onFilterChange: (filter: JudicialFilter) => void | Promise<void>
  selectedTab: JudicialTab
  onTabChange: (tab: JudicialTab) => void | Promise<void>
  preanalysis: JudicialPreanalysisListViewProps
}

const TABS: JudicialTab[] = ['preanalysis', 'verifications']

export const JudicialResultListView = ({
  records,
  regionName,
  onBackToPiloteClicked,
  onCandidateSearch,
  className,
  requestedResultsCount = 0,
  queriedCandidatesCount = 0,
  pricePerSearch,
  pricePerRecord,
  selectedFilter,
  onFilterChange,
  selectedTab,
  onTabChange,
  preanalysis,
}: JudicialResultListViewProps) => {
  const intl = useIntl()
  const getDecisionText = useDecisionText()
  const exportCsv = useExportJudicialRecordsCsv()

  const sixMonthsAgo = useMemo(() => dayjs().subtract(6, 'month'), [])
  const getDecisionTextColor = decisionTextColor(sixMonthsAgo)

  const filteredRecords = useMemo(
    () =>
      records.filter((record) => {
        switch (selectedFilter) {
          case 'completed':
            return (
              record.decision !== 'PRELIMINARY_ACCEPTANCE' &&
              (!record.decision || dayjs(record.lastDecisionDate).isBefore(sixMonthsAgo))
            )
          case 'accepted':
            return (
              (record.decision === 'ACCEPT_ESCORT' ||
                record.decision === 'ACCEPT_PARTNER' ||
                record.decision === 'COMPLETE_ACCEPTANCE') &&
              dayjs(record.lastDecisionDate).isAfter(sixMonthsAgo)
            )
          case 'completeRefusal':
            return record.decision === 'COMPLETE_REFUSAL' && dayjs(record.lastDecisionDate).isAfter(sixMonthsAgo)
          default:
            return true
        }
      }),
    [records, selectedFilter, sixMonthsAgo]
  )

  const searchesCost = queriedCandidatesCount * pricePerSearch
  const recordsCost = requestedResultsCount * pricePerRecord
  const totalCost = searchesCost + recordsCost

  const formatCurrency = (value: number) =>
    intl.formatNumber(value, {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      currencyDisplay: 'narrowSymbol',
    })

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <AdminHeader onBackToPiloteClicked={onBackToPiloteClicked} regionName={regionName}>
        <div className="flex items-center gap-12">
          <div className="flex items-center gap-2">
            <div className="flex flex-col gap-1 text-[12px] leading-[1.2] text-gray-200">
              <span>{intl.formatMessage({ id: 'judicial.results.searchesLabel' })}</span>
              <span>{intl.formatMessage({ id: 'judicial.results.consultedLabel' })}</span>
            </div>
            <div className="flex flex-col gap-1 border-r border-gray-500 pr-4 pl-1 text-right text-[14px] leading-none font-bold tracking-[-0.42px] text-white">
              <span>{queriedCandidatesCount}</span>
              <span>{requestedResultsCount}</span>
            </div>
            <div className="flex flex-col gap-1 border-r border-gray-500 pr-4 pl-1 text-right text-[14px] leading-none font-bold tracking-[-0.42px] text-white">
              <span>{formatCurrency(searchesCost)}</span>
              <span>{formatCurrency(recordsCost)}</span>
            </div>
          </div>
          <div className="flex items-center gap-2.5 text-[14px] text-white">
            <span className="leading-none tracking-[-0.42px] uppercase">
              {intl.formatMessage({ id: 'judicial.results.totalCost' })}
            </span>
            <span className="leading-[1.2] font-bold">{formatCurrency(totalCost)}</span>
          </div>
        </div>
      </AdminHeader>

      <div className="flex min-h-0 flex-1 flex-col px-16 py-6">
        <div className="mb-6 shrink-0">
          <div className="flex items-center gap-4">
            <h1 className="mb-4 text-xl font-normal text-gray-800">
              {intl.formatMessage({ id: 'judicial.results.pageTitle' })}
            </h1>
          </div>

          <div className="mb-6 flex gap-2" role="tablist">
            {TABS.map((tab) => (
              <NavLink
                key={tab}
                role="tab"
                aria-selected={selectedTab === tab}
                state={selectedTab === tab ? 'active' : 'default'}
                onClick={() => onTabChange(tab)}>
                {intl.formatMessage({ id: `judicial.tabs.${tab}` })}
              </NavLink>
            ))}
          </div>

          {selectedTab === 'verifications' && (
            <div className="flex items-center gap-4">
              <JudicialVolunteerSearchForm onFormSubmit={onCandidateSearch} />
              <div className="ml-auto flex items-center gap-3">
                <Select value={selectedFilter} onValueChange={onFilterChange}>
                  <SelectTrigger className="h-8 w-56 rounded-2xl border border-gray-300 bg-gray-100 text-sm">
                    <SelectValue placeholder={intl.formatMessage({ id: 'judicial.results.filter.allRequests' })} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {intl.formatMessage({ id: 'judicial.results.filter.allRequests' })}
                    </SelectItem>
                    <SelectItem value="completed">
                      {intl.formatMessage({ id: 'judicial.results.filter.completed' })}
                    </SelectItem>
                    <SelectItem value="accepted">
                      {intl.formatMessage({ id: 'judicial.results.filter.accepted' })}
                    </SelectItem>
                    <SelectItem value="completeRefusal">
                      {intl.formatMessage({ id: 'judicial.results.filter.completeRefusal' })}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  onClick={() => {
                    exportCsv(filteredRecords)
                  }}
                  title={intl.formatMessage({ id: 'judicial.results.exportCsv' })}>
                  <Download className="size-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {selectedTab === 'preanalysis' ? (
          <JudicialPreanalysisListView {...preanalysis} />
        ) : (
          <div className="min-h-0 flex-1 overflow-auto pb-6">
            <Table className="rounded-lg border border-gray-200">
              <TableHeader>
                <TableRow>
                  <TableHead className="max-w-16 text-center text-base font-bold">
                    {intl.formatMessage({ id: 'judicial.results.table.volunteer' })}
                  </TableHead>
                  <TableHead className="text-base font-bold">
                    {intl.formatMessage({ id: 'judicial.results.table.nameFirstName' })}
                  </TableHead>
                  <TableHead className="max-w-24 text-center text-base font-bold">
                    {intl.formatMessage({ id: 'judicial.results.table.birthDate' })}
                  </TableHead>
                  <TableHead className="max-w-32 text-center text-base font-bold">
                    {intl.formatMessage({ id: 'judicial.results.table.municipalSearchStatus' })}
                  </TableHead>
                  <TableHead className="max-w-32 text-center text-base font-bold">
                    {intl.formatMessage({ id: 'judicial.results.table.provincialSearchStatus' })}
                  </TableHead>
                  <TableHead className="text-center text-base font-bold">
                    {intl.formatMessage({ id: 'judicial.results.table.decision' })}
                  </TableHead>
                  <TableHead className="max-w-24 text-center text-base font-bold">
                    {intl.formatMessage({ id: 'judicial.results.table.lastDecision' })}
                  </TableHead>
                  <TableHead className="max-w-24 text-center text-base font-bold">
                    {intl.formatMessage({ id: 'judicial.results.table.requestTimestamp' })}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecords.map((record) => (
                  <TableRow key={record._id} className="hover:bg-gray-50">
                    <TableCell className="text-center text-blue-700 uppercase select-none">
                      <a href={volunteerPiloteUrl(record)} target="_blank" className="underline" rel="noreferrer">
                        {record.volunteer.code}
                      </a>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-normal text-gray-900">
                        {record.volunteer.lastName}, {record.volunteer.firstName}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {record.volunteer.birthDate && (
                        <span className="text-sm font-normal text-gray-900">
                          {dayjs.utc(record.volunteer.birthDate).format('YYYY-MM-DD')}
                        </span>
                      )}
                    </TableCell>
                    <MunicipalSearchStatusCell record={record} />
                    <ProvincialSearchStatusCell record={record} />
                    <TableCell className="text-center">
                      {record.decision && (
                        <span className={cn('text-sm', getDecisionTextColor(record.decision, record.lastDecisionDate))}>
                          {getDecisionText(record.decision)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {record.lastDecisionDate && (
                        <span className={cn('text-sm', getDecisionTextColor(record.decision, record.lastDecisionDate))}>
                          {dayjs.utc(record.lastDecisionDate).format('YYYY-MM-DD')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {
                        <span className="text-sm font-normal text-gray-900">
                          {dayjs(record.requestTimestamp).format('YYYY-MM-DD HH:mm')}
                        </span>
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  )
}
